/**
 * RecipientItem Utility Functions
 *
 * Pure utility functions extracted from the RecipientItem component.
 * These functions have no side effects and are easily testable.
 */

import {
  AlertTriangleIcon,
  AlertCircleIcon,
  InfoIcon,
  CheckCircleIcon,
} from '@shopify/polaris-icons';
import type { AlertLevel } from '../selectors';
import { VERIFICATION_CODE_PATTERN, VERIFICATION_CODE_LENGTH } from './constants';

// =============================================================================
// Name/Avatar Utilities
// =============================================================================

/**
 * Get initials from a person's name
 * - For two or more words: first letter of first and last word
 * - For single word: first two characters
 *
 * @param name - The person's full name
 * @returns Uppercase initials (1-2 characters)
 */
export function getInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '??';

  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    const first = parts[0][0] || '';
    const last = parts[parts.length - 1][0] || '';
    return (first + last).toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

// =============================================================================
// Alert Level Utilities
// =============================================================================

/**
 * Get the icon component for an alert level
 *
 * @param level - The alert level
 * @returns The Polaris icon component
 */
export function getAlertLevelIcon(level: AlertLevel) {
  switch (level) {
    case 'urgent':
      return AlertTriangleIcon;
    case 'warning':
      return AlertCircleIcon;
    case 'info':
      return InfoIcon;
    case 'healthy':
      return CheckCircleIcon;
  }
}

/**
 * Get the badge tone for an alert level
 *
 * @param level - The alert level
 * @returns The Polaris badge tone
 */
export function getAlertLevelTone(level: AlertLevel): 'critical' | 'warning' | 'info' | 'success' {
  switch (level) {
    case 'urgent':
      return 'critical';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    case 'healthy':
      return 'success';
  }
}

/**
 * Get the readable label for an alert level
 *
 * @param level - The alert level
 * @returns Human-readable label
 */
export function getAlertLevelLabel(level: AlertLevel): string {
  switch (level) {
    case 'urgent':
      return 'Urgent';
    case 'warning':
      return 'Warning';
    case 'info':
      return 'Info';
    case 'healthy':
      return 'Restock';
  }
}

/**
 * Sort alert levels by severity (most severe first)
 *
 * @param levels - Array of alert levels
 * @returns Sorted array with urgent > warning > info > healthy
 */
export function sortAlertLevelsBySeverity(levels: AlertLevel[]): AlertLevel[] {
  const order: Record<AlertLevel, number> = {
    urgent: 0,
    warning: 1,
    info: 2,
    healthy: 3,
  };
  return [...levels].sort((a, b) => order[a] - order[b]);
}

// =============================================================================
// Verification Utilities
// =============================================================================

/**
 * Validate a verification code format
 *
 * @param code - The code to validate
 * @returns True if code is valid 6-digit format
 */
export function isValidVerificationCode(code: string): boolean {
  return VERIFICATION_CODE_PATTERN.test(code);
}

/**
 * Format verification code input (digits only)
 *
 * @param input - Raw input string
 * @returns Formatted string with only digits, max length 6
 */
export function formatVerificationCodeInput(input: string): string {
  return input.replace(/\D/g, '').slice(0, VERIFICATION_CODE_LENGTH);
}

/**
 * Format countdown time for display
 *
 * @param seconds - Remaining seconds
 * @returns Formatted string like "0:45" or "1:00"
 */
export function formatResendCountdown(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// =============================================================================
// Channel Utilities
// =============================================================================

/**
 * Get display label for channel type
 *
 * @param channelType - 'digest' or 'instant'
 * @returns Human-readable label
 */
export function getChannelLabel(channelType: 'digest' | 'instant'): string {
  return channelType === 'digest' ? 'Digest' : 'Instant';
}

/**
 * Get description for channel type
 *
 * @param channelType - 'digest' or 'instant'
 * @returns Description text
 */
export function getChannelDescription(channelType: 'digest' | 'instant'): string {
  return channelType === 'digest'
    ? 'Daily summary of all alerts'
    : 'Immediate notification for each alert';
}

// =============================================================================
// Date/Time Utilities
// =============================================================================

/**
 * Format a date string for display
 *
 * @param dateString - ISO date string
 * @returns Formatted date like "Jan 15, 2025"
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a date string with time
 *
 * @param dateString - ISO date string
 * @returns Formatted date like "Jan 15, 2025 at 3:45 PM"
 */
export function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Get relative time description
 *
 * @param dateString - ISO date string
 * @returns Relative time like "2 hours ago" or "3 days ago"
 */
export function getRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;

  return formatDate(dateString);
}
